/**
 * Theme Utility Functions
 * Centralized theme management for applying theme colors as CSS variables
 */

const DEFAULT_BG = "#3B82F6";
const DEFAULT_TEXT = "#FFFFFF";

/**
 * Apply theme CSS variables to the root element
 * @param {Object} theme - Theme object with backgroundColor, textColor, etc.
 */
export const applyThemeCSSVariables = (theme) => {
  if (!theme) return;

  const root = document.documentElement;
  let bg = theme.backgroundColor || DEFAULT_BG;
  let text = theme.textColor || DEFAULT_TEXT;

  // Hardcoded values based on themeId
  if (theme.themeId === 1) {
    bg = "#3B82F6";
    text = "#FFFFFF";
  } else if (theme.themeId === 2) {
    bg = "#FFFFFF";
    text = "#000000";
  }

  // Set a specific dark background for the sidebar when the Dark theme is selected
  const isDarkTheme = theme.themeName === "Dark" || theme.themeCode === "Dark";
  const sidebarBg = isDarkTheme ? "#2C323F" : bg;

  // Canonical variables — used by the wildcard rules in theme-variables.css
  root.style.setProperty("--theme-bg-color", bg);
  root.style.setProperty("--theme-text-color", text);
  root.style.setProperty("--theme-sidebar-bg-color", sidebarBg);

  // For light themes where bg is white/off-white, buttons and table headers need a primary color
  const isLight = theme.themeName === "Light" || bg.toLowerCase() === "#f8f9fa" || bg.toLowerCase() === "#ffffff";
  const primaryBg = isLight ? "#d1d1d1ff" : bg;
  const primaryText = text;

  // Table headers for Light theme: lighter grey than buttons
  const tableHeaderBg = isLight ? "#e5e7eb" : primaryBg;
  const tableHeaderText = text;

  // Accordion headers (TableHeaderTheme) for Light theme: white background
  let accordionHeaderBg = isLight ? "#FFFFFF" : primaryBg;
  if (isDarkTheme) {
    accordionHeaderBg = "#3c4250ff";
  }

  root.style.setProperty("--theme-primary-element-bg", primaryBg);
  root.style.setProperty("--theme-primary-element-text", primaryText);
  root.style.setProperty("--theme-table-header-bg", tableHeaderBg);
  root.style.setProperty("--theme-table-header-text", tableHeaderText);
  root.style.setProperty("--theme-accordion-header-bg", accordionHeaderBg);

  // Active menu item styles (sidebar)
  let activeMenuBg = isLight
    ? "color-mix(in srgb, var(--theme-primary-element-bg) 35%, transparent)"
    : "color-mix(in srgb, var(--theme-sidebar-bg-color) 80%, black)";

  if (theme.themeId === 1) {
    activeMenuBg = "#3B82F6";
  }
  const activeMenuText = isLight ? "#1a1a1a" : text;
  // Border accent: black for light, white/theme-text for dark so it's always visible
  const activeBorderColor = isLight ? "#000000" : text;

  root.style.setProperty("--theme-active-menu-bg", activeMenuBg);
  root.style.setProperty("--theme-active-menu-text", activeMenuText);
  root.style.setProperty("--theme-active-border-color", activeBorderColor);

  // Nav icon / text color — black for light themes so icons are visible on a white sidebar
  const navTextColor = isLight ? "#1a1a1a" : text;
  root.style.setProperty("--theme-nav-text-color", navTextColor);

  // Icon image filter — convert PNG icons to dark/black for light themes
  // (icons are <img> tags so CSS color doesn't apply; we use filter instead)
  let iconFilter = isLight
    ? "brightness(0) saturate(100%)"
    : "none";

  let activeIconFilter = isLight ? "brightness(0) saturate(100%)" : "none";
  if (theme.themeId === 1) {
    activeIconFilter = "brightness(0) invert(1)"; // force white only for active/hover
  }

  root.style.setProperty("--theme-icon-filter", iconFilter);
  root.style.setProperty("--theme-active-icon-filter", activeIconFilter);

  // Border for light theme sidebar
  const sidebarBorderRight = isLight ? "1px solid #e5e7eb" : "none";
  root.style.setProperty("--theme-sidebar-border-right", sidebarBorderRight);
  const sidebarBrandDivider = isLight
    ? "rgba(156, 163, 175, 0.55)"
    : "rgba(255, 255, 255, 0.08)";
  root.style.setProperty("--theme-sidebar-brand-divider", sidebarBrandDivider);

  // Backward-compatibility aliases for files that still reference old variable names
  root.style.setProperty("--theme-primary-color", primaryBg);
  root.style.setProperty("--theme-sidebar-bg", sidebarBg);
  root.style.setProperty("--theme-sidebar-text", text);
};

/**
 * Get CSS variable value
 * @param {string} variableName - Name of the CSS variable (e.g. '--theme-primary-color')
 * @returns {string} The value of the CSS variable
 */
export const getCSSVariable = (variableName) => {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(
    variableName
  );
};

/**
 * Initialize theme on app load by reading from localStorage
 */
export const initializeTheme = () => {
  try {
    const rawTheme = localStorage.getItem("theme");
    if (rawTheme) {
      const parsedTheme = JSON.parse(rawTheme);
      applyThemeCSSVariables(parsedTheme);
    }
  } catch (error) {
    console.error("Error initializing theme from localStorage", error);
  }
};
